import React, { useEffect, useState } from 'react'
import { FaEdit, FaEye, FaEyeSlash } from 'react-icons/fa'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  allCustomise,
  deleteCustomise,
  getAllHideCustomise,
  restoreReport,
  sendMail,
} from '../../api/customiseApi'
import { handleDate } from '../../handleDateAndPdf'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'

export default function Customise() {
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)
  const [showHidden, setShowHidden] = useState(false)

  useEffect(() => {
    document.title = 'Customise Report'
  }, [])

  const {
    data: customiseData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['customise', showHidden], // Array format recommended
    queryFn: () => (showHidden ? getAllHideCustomise() : allCustomise()),

    onError: (error) => {
      console.error('Error fetching customise:', error)
    },
  })

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteCustomise(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch customises
      queryClient.invalidateQueries({ queryKey: ['customise'] })

      // Show success message
      toast.success('customise hide successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Hide!',
        text: 'The customise has been hide successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
    },
    onError: (err) => {
      console.error('Hide error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to hide customise. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Hide customise',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Hiding...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or hide failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Hide cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (customiseId, customiseTitle) => {
    setToDelete(customiseId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Hide customise?',
      html: `
        <p>Are you sure you want to hide this customise?</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
          <strong style="color: #dc2626;">${customiseTitle}</strong>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          This action will send an OTP to your email for verification.
        </p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(customiseId)
      } else {
        setToDelete(null)
      }
    })
  }

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customise'] })
      toast.success('Report restore sucessfully!')
    },
    onError: (err) => {
      console.error('restore report error:', err)
      toast.error(err.message || 'Failed to send verification email')
    },
  })

  if (isLoading) {
    return (
      <>
        <Breadcrum heading="DSJ Insight" subheading="Customise Report" />
        <div className="text-center py-8">Loading Customise...</div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum heading="DSJ Insight" subheading="Customise Report" />
        <div className="text-center py-8 text-red-500">
          Error loading Customise. Please try again later.
        </div>
      </>
    )
  }

  const handleToggle = () => {
    setShowHidden((prev) => !prev)
  }

  return (
    <>
      <Breadcrum subheading="Customise Report" heading="DSJ Insight" />
      <div className="p-6 max-w-6xl mx-auto bg-white rounded shadow">
        <div className="md:w-full mx-auto flex justify-between items-center">
          <h2
            className={`text-2xl font-semibold ${
              showHidden ? 'text-red-600' : 'text-gray-600'
            }`}
          >
            {showHidden ? 'Hidden' : 'Active'} Files
          </h2>

          <button
            onClick={handleToggle}
            title={showHidden ? 'Show active reports' : 'Show hidden reports'}
            className="text-white bg-blue-500 p-2 rounded hover:bg-blue-700 transition-colors"
          >
            {showHidden ? <FaEye /> : <FaEyeSlash />}
          </button>
        </div>
        <div className="my-6  overflow-x-auto whitespace-nowrap">
          <table className="min-w-full bg-white border border-gray-200 text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
              <tr>
                <th className="px-4 py-3 border-b">Title</th>
                <th className="px-4 py-3 border-b">Company name</th>
                <th className="px-4 py-3 border-b">Price</th>
                <th className="px-4 py-3 border-b">User email</th>
                <th className="px-4 py-3 border-b">Created At</th>
                <th className="px-4 py-3 border-b">Created By</th>
                <th className="px-4 py-3 border-b">Updated At</th>
                <th className="px-4 py-3 border-b">Updated By</th>
                <th className="px-4 py-3 border-b">Deleted At</th>
                <th className="px-4 py-3 border-b">Deleted By</th>
                <th className="px-4 py-3 border-b text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customiseData.map((report, index) => (
                <tr key={report.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{report.title}</td>
                  <td className="px-4 py-3">{report.company}</td>
                  <td className="px-4 py-3">
                    &#8377;
                    {report.price}
                  </td>
                  <td className="px-4 py-3">{report.userEmail}</td>

                  <td className="py-3 px-4 border-b font-medium">
                    {handleDate(report.createdAt)}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {report?.createAdmin}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {handleDate(report.updatedAt)}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {report?.updateAdmin}
                  </td>

                  <td className="py-3 px-4 border-b font-medium">
                    {handleDate(report.deletedAt)}
                  </td>
                  <td className="py-3 px-4 border-b font-medium">
                    {report?.deletedBy}
                  </td>
                  <td className="px-4 py-3 text-center flex justify-center gap-2">
                    {report?.isDeleted === false && (
                      <Link
                        to={`/dealdashboard/add/customise/${report.id}`}
                        className="bg-yellow-400 hover:bg-yellow-500 text-white p-2 rounded"
                      >
                        <FaEdit size={16} />
                      </Link>
                    )}
                    {report?.isDeleted === true ? (
                      <button
                        title="Restore report"
                        onClick={() => restoreMutation.mutate(report.id)}
                        className="text-white bg-green-700 p-2 rounded hover:bg-green-900 transition-colors"
                      >
                        <FaEye />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteClick(report.id, report.title)
                        }
                        className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                          sendingMail || deleteMutation.isPending
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        title="Hide customise"
                        disabled={sendingMail || deleteMutation.isPending}
                      >
                        {sendingMail && toDelete === report.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                        ) : (
                          <FaEyeSlash />
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>
      {showHidden ? '' : <AddItem redirect="/dealdashboard/add/customise" />}
    </>
  )
}
